import { useDispatch, useSelector } from 'react-redux';
import Loading from '../Loading';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';

import {
    fetchCategories,
    Product,
    selectCategories,
    selectIsCategoriesStartLoad,
} from '../../ducks/shop';
import { gettext, interpolate } from '../../utils/text';
import { addToCart, selectors as cartSelectors } from '../../ducks/cart';
import { Cart } from './types';
import * as Tabs from '@radix-ui/react-tabs';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { readCategoryFromUrl } from './utils';
import { useProducts } from './hooks';
import * as AccessibleIcon from '@radix-ui/react-accessible-icon';
import { Minus, Plus, ArrowRight } from 'lucide-react';
import { clsx } from 'clsx';

type FoodMenuProps = {
    selectedShowId?: number | null;
};

type QuantityButtonProps = {
    item: Product;
    quantity: number;
    handleQuantityChange: (itemId: number, delta: number) => void;
};

const QuantityButton = ({
    item,
    quantity,
    handleQuantityChange,
}: QuantityButtonProps) => {
    return (
        <div
            className="btn-group"
            role="group"
            aria-label={gettext('Quantity')}
        >
            <button
                type="button"
                className="quantity-btn"
                onClick={() => handleQuantityChange(item.id, -1)}
                disabled={quantity === 0}
            >
                <AccessibleIcon.Root label={gettext('Decrease quantity')}>
                    <Minus size={12} strokeWidth={2} aria-hidden="true" />
                </AccessibleIcon.Root>
            </button>
            <span className="px-3 py-1 fw-bold fs-5">{quantity}</span>
            <button
                type="button"
                className="quantity-btn"
                onClick={() => handleQuantityChange(item.id, +1)}
            >
                <AccessibleIcon.Root label={gettext('Increase quantity')}>
                    <Plus size={12} strokeWidth={2} aria-hidden="true" />
                </AccessibleIcon.Root>
            </button>
        </div>
    );
};

type AddToCartButtonProps = {
    item: Product;
    quantity: number;
    handleAddToCart: (item: Product) => void;
};

const AddToCartButton = ({
    item,
    quantity,
    handleAddToCart,
}: AddToCartButtonProps) => (
    <button
        type="button"
        className="btn btn-danger bg-crimson text-uppercase fw-bold d-inline-flex gap-2"
        // Note: item.name contains full product description with HTML,
        // which would be too verbose for screen readers, so we keep
        // the aria-label generic and don't reference item.name in aria attributes
        aria-label={
            quantity === 0
                ? gettext('Add to cart (select quantity first)')
                : interpolate(gettext('Add %(quantity)s product to cart'), {
                      quantity,
                  })
        }
        onClick={() => handleAddToCart(item)}
        disabled={quantity === 0}
    >
        <span className="text-nowrap">{gettext('Add to cart')}</span>
        <ArrowRight
            size={16}
            strokeWidth={2}
            color="white"
            aria-hidden="true"
        />
    </button>
);

type FoodMenuItemProps = {
    item: Product;
    quantity: number;
    handleQuantityChange: (itemId: number, delta: number) => void;
    handleAddToCart: (item: Product) => void;
};

const FoodMenuItem = ({
    item,
    quantity,
    handleQuantityChange,
    handleAddToCart,
}: FoodMenuItemProps) => {
    const nameId = `menu-item-name-${item.id}`;

    return (
        <article className="col-12 px-0" aria-labelledby={nameId}>
            <div className="py-4 border-bottom border-end">
                <div className="row g-3 align-items-center">
                    <div className="col-12 col-sm-4">
                        <img
                            src={item.thumbnail || item.photo}
                            className="d-block object-fit-cover"
                            alt=""
                            width={130}
                            height={130}
                        />
                    </div>

                    <div className="col-12 col-sm-8 d-flex flex-column justify-content-between gap-3 pe-4">
                        <div className="d-flex align-items-start justify-content-between gap-2">
                            <div
                                className="opacity-75"
                                id={nameId}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(item.name),
                                }}
                            />
                            <p className="text-end text-danger fw-bold fs-5 mb-0 text-nowrap">
                                {item.price.formatted.gross}
                            </p>
                        </div>

                        <div className="d-flex justify-content-between align-items-end gap-2">
                            <QuantityButton
                                item={item}
                                quantity={quantity}
                                handleQuantityChange={handleQuantityChange}
                            />
                            <AddToCartButton
                                item={item}
                                quantity={quantity}
                                handleAddToCart={handleAddToCart}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
};

const FoodMenu = ({ selectedShowId }: FoodMenuProps) => {
    const dispatch = useDispatch();
    const categories = useSelector(selectCategories);
    const isCategoriesLoading = useSelector(selectIsCategoriesStartLoad);
    const cart = useSelector(cartSelectors.cart) as Cart | null;

    const getCartQuantities = useCallback((): Record<number, number> => {
        const cartQuantities: Record<number, number> = {};
        if (cart && cart.lines.length) {
            for (const line of cart.lines) {
                cartQuantities[line.product.id] = line.quantity;
            }
        }
        return cartQuantities;
    }, [cart]);

    const [activeCategory, setActiveCategory] = useState<number | null>(null);
    const initialCategoryFromUrl = useMemo(readCategoryFromUrl, []);
    const [quantities, setQuantities] = useState<Record<number, number>>(() =>
        getCartQuantities(),
    );
    const [statusMessage, setStatusMessage] = useState<string>('');

    const { items, loading: loadingItems, error } = useProducts(
        activeCategory,
        selectedShowId,
    );

    useEffect(() => {
        dispatch(fetchCategories());
    }, [dispatch]);

    useEffect(() => {
        if (!categories.length) {
            return;
        }

        if (activeCategory === null) {
            const preferred =
                initialCategoryFromUrl &&
                categories.some(cat => cat.id === initialCategoryFromUrl)
                    ? initialCategoryFromUrl
                    : categories[0].id;
            setActiveCategory(preferred);
            return;
        }

        if (!categories.some(cat => cat.id === activeCategory)) {
            setActiveCategory(categories[0].id);
        }
    }, [categories, activeCategory, initialCategoryFromUrl]);

    useEffect(() => {
        if (activeCategory === null || typeof window === 'undefined') {
            return;
        }

        const url = new URL(window.location.href);
        url.searchParams.set('category', String(activeCategory));
        window.history.replaceState(null, '', url.toString());
    }, [activeCategory]);

    const handleQuantityChange = (itemId: number, delta: number) => {
        setQuantities(prev => {
            const current = prev[itemId] ?? 0;
            const next = Math.max(0, current + delta);
            return { ...prev, [itemId]: next };
        });
        setStatusMessage(gettext('Quantity updated.'));
    };

    const handleAddToCart = async (item: Product) => {
        const quantity = quantities[item.id] ?? 0;
        if (quantity <= 0) {
            setStatusMessage(
                gettext('Please choose quantity for a product before adding.'),
            );
            return;
        }

        try {
            const result = (await dispatch(
                addToCart({ product: item.id, quantity }),
            )) as any;
            if (result?.success !== false) {
                setStatusMessage(
                    interpolate(
                        gettext('%(quantity)s product(s) added to cart.'),
                        {
                            quantity,
                        },
                    ),
                );
            } else {
                setStatusMessage(
                    gettext(
                        'Failed to add product to cart. Please try again later.',
                    ),
                );
            }
        } catch (error) {
            setStatusMessage(
                gettext(
                    'Failed to add product to cart. Please try again later.',
                ),
            );
        }
    };

    if (
        isCategoriesLoading ||
        (categories.length > 0 && activeCategory === null)
    ) {
        return <Loading />;
    }

    return (
        <main className="container py-2" aria-label={gettext('Food menu')}>
            {/* Live region for status messages */}
            <VisuallyHidden asChild>
                <p aria-live="polite" role="status">
                    {statusMessage}
                </p>
            </VisuallyHidden>

            <Tabs.Root
                orientation="vertical"
                value={
                    activeCategory !== null ? String(activeCategory) : undefined
                }
                onValueChange={value => setActiveCategory(Number(value))}
            >
                <div className="row g-4">
                    <nav
                        className="col-12 col-md-4 border-end"
                        aria-label={gettext('Menu categories')}
                    >
                        <h2 className="h5 px-3 pt-4">
                            {gettext('Categories')}
                        </h2>
                        <Tabs.List className="list-group list-group-flush">
                            {categories.map(category => {
                                const isActive = category.id === activeCategory;
                                return (
                                    <Tabs.Trigger
                                        key={category.id}
                                        value={String(category.id)}
                                        className={clsx(
                                            'list-group-item list-group-item-action border-0 rounded-0 py-3 px-3 text-start text-nowrap fw-medium',
                                            isActive
                                                ? 'active bg-crimson text-white'
                                                : 'bg-transparent',
                                        )}
                                    >
                                        {category.name}
                                    </Tabs.Trigger>
                                );
                            })}
                        </Tabs.List>
                    </nav>

                    <section
                        className="col-12 col-md-8"
                        aria-label={gettext('Menu items')}
                    >
                        {categories.map(category => (
                            <Tabs.Content
                                key={category.id}
                                value={String(category.id)}
                                aria-busy={
                                    category.id === activeCategory &&
                                    loadingItems
                                        ? 'true'
                                        : 'false'
                                }
                            >
                                {category.id === activeCategory && (
                                    <>
                                        {loadingItems && (
                                            <div
                                                className="row"
                                                role="status"
                                                aria-label={gettext(
                                                    'Loading items…',
                                                )}
                                            >
                                                {[1, 2, 3].map(index => (
                                                    <div
                                                        key={index}
                                                        className="col-12 px-0 border-end"
                                                    >
                                                        <div className="py-4 border-bottom border-end">
                                                            <div className="row g-3 align-items-center">
                                                                <div className="col-12 col-sm-4">
                                                                    <div
                                                                        className="placeholder bg-secondary"
                                                                        style={{
                                                                            width:
                                                                                '130px',
                                                                            height:
                                                                                '130px',
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="col-12 col-sm-8 pe-4">
                                                                    <div className="placeholder-glow d-flex flex-column gap-2">
                                                                        <span className="placeholder bg-secondary col-7" />
                                                                        <span className="placeholder bg-secondary col-4" />
                                                                        <span className="placeholder bg-secondary col-6" />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {error && (
                                            <p
                                                className="text-danger"
                                                role="alert"
                                            >
                                                {error}
                                            </p>
                                        )}

                                        {!loadingItems &&
                                            !error &&
                                            items.length === 0 && (
                                                <p>
                                                    {gettext(
                                                        'No items in this category for now.',
                                                    )}
                                                </p>
                                            )}

                                        {!loadingItems && (
                                            <div className="row">
                                                {items.map(item => (
                                                    <FoodMenuItem
                                                        key={item.id}
                                                        item={item}
                                                        quantity={
                                                            quantities[
                                                                item.id
                                                            ] ?? 0
                                                        }
                                                        handleQuantityChange={
                                                            handleQuantityChange
                                                        }
                                                        handleAddToCart={
                                                            handleAddToCart
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </Tabs.Content>
                        ))}
                    </section>
                </div>
            </Tabs.Root>
        </main>
    );
};

export default FoodMenu;
